import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faSortAsc, faSortDesc } from "@fortawesome/free-solid-svg-icons";
import { Trans, useTranslation } from "react-i18next";
import { useSelector, useDispatch } from 'react-redux';
import { alternateAddPropertyKey, getPropertySelectionList, setCurrentAddress, setCurrentOwnerType, setCurrentProperty, setRedirects } from "../../../redux/actions/propertySelectionList/propertySelectionList";
import { getViewPropertyDetails } from "../../../redux/actions/viewPropertyDetails/viewPropertyDetails";
import { getPropertyDocuments } from "../../../redux/actions/propertyDocuments/propertyDocuments";
import { getReports } from "../../../redux/actions/reports/reports";
import { RootState } from "../../../redux/reducers/root";
import AmpInput from "../../common/AmpInput/AmpInput";
import AmpButton from "../../common/AmpButton/AmpButton";
import AmpLoadingSpinner from "../../common/AmpLoadingSpinner/AmpLoadingSpinner";
import { useLocation, useNavigate } from "react-router-dom";
import AmpInlineLoadingSpinner from "../../common/AmpInlineLoadingSpinner/AmpInlineLoadingSpinner";
import "./PropertySelectionList.css";
import { setToastNotification } from "../../../redux/actions/toastNotification/toastNotification";
import { changeIsCloseable, changeModalContent, changeModalState } from "../../../redux/actions/modal/modal";
import AddProperty from "../../AddProperty/AddProperty";
import { downloadResidentialProfilePdf, resetResidentialProfile } from "../../../redux/actions/documents/resendentialProfile";
import { ResidentialProfileState } from "../../../redux/reducers/getDocumentReducer/residentialProfile";
import { getCurrentPropertyDetails } from "../../../redux/actions/currentPropertyDetails/currentPropertyDetails";
import RemoveProperty from "../../RemoveProperty/RemoveProperty";
import { downloadTenantProfile } from "../../../redux/actions/tenantProfile/tenantProfile";
import AmpPagination from "../../common/AmpPagination/AmpPagination";
import { filterPropertyList } from "../../../utils/filterPropertyList";
import { UserProperty } from "../../../redux/reducers/getPropertySelectionListReducer/propertySelectionListReducer";

interface PropertySelectionListProps {
    showFullVersion: boolean,
    onPropertySelected?: () => void
    passedPropertyList?: UserProperty[]  // Pass a hard coded list for testing
}

export default function PropertySelectionList(props: PropertySelectionListProps): JSX.Element {

    const { showFullVersion, onPropertySelected, passedPropertyList } = props;

    const { t } = useTranslation('propertySelectionList');

    const [currentPage, setCurrentPage] = useState<number>(1);

    const [searchText, setSearchText] = useState<string>("");

    const [sortCriteria, setSortCriteria] = useState<[1 | 2 | 3, "ASC" | "DESC"]>([1, "ASC"]);

    const recordsPerPage = 5;

    const propertySelectionList = useSelector((state: RootState) => state.propertyListReducer.propertySelectionList);

    const currentProperty = useSelector((state: RootState) => state.propertyListReducer.currentProperty);

    const modalState = useSelector((state: RootState) => state.modalReducer);

    const downloadPdf = useSelector((state: RootState) => state.docReducer.downloadPdf) as ResidentialProfileState;

    const manageUserInfoState: Record<string, unknown> = useSelector((state: RootState) => state.authReducer.getUser);

    const dispatch = useDispatch();

    const propertyList: UserProperty[] = passedPropertyList ?? propertySelectionList['properties'];
    // const propertyIsSharedList: Record<string, string>[] = propertySelectionList['property_is_shared'];
    // const propertyIsSharedRecord: Record<string, boolean> = {}

    const responseStatus = propertySelectionList['status'];

    const responseMessage = propertySelectionList['message'];

    const redirects: boolean = propertySelectionList['redirects'];

    const propertyListLoading: boolean = propertySelectionList['loading'];

    const currentRoll: string = currentProperty['currentProperty'];

    const currentOwnerType: string = currentProperty['currentOwnerType'];

    const modalContentAriaLabel: string = modalState['contentAriaLabel'];

    const modalIsCloseable: boolean = modalState['isCloseable'];

    const navigate = useNavigate();

    const location = useLocation();

    const [currentDeleted, setCurrentDeleted] = useState(false);
    const [deletedRoll, setLastDeletedRoll] = useState("");

    const [currentDownload, setCurrentDownload] = useState("");

    useEffect(() => {

        const isNoPropPath = ["/amp/profile", "/amp/contact-us", "/amp/property-assessment", "/amp"].includes(location.pathname);

        // No properties on account flow, only allows pages where no property is obtained 
        if (propertyList?.length === 0
            && propertyList
            && !propertyListLoading
            && !isNoPropPath
        ) {
            navigate("/amp/profile");
            dispatch(changeModalState("modal-fade-out"));
        } else if (isNoPropPath && (propertyList?.length === 0)) {
            dispatch(changeModalState("modal-fade-out"));
        }
        // eslint-disable-next-line 
    }, [location]);

    useEffect(() => {
        // Users currently selected property has been deleted
        if (currentDeleted) {
            // Grab first property in the list
            // Edge case condition to make sure we don't select the first property if we deleted the first property
            const property = propertyList[0].roll_num !== deletedRoll ? propertyList[0] : propertyList[1];
            selectProperty(
                property.roll_num as string,
                property.address as string,
                property.property_id as string,
                property.owner_type as string,
                property.property_code as string,
                property.group_id as string
            );
            setCurrentDeleted(false);
            setLastDeletedRoll("");
            dispatch(setRedirects(false));
        }
        // eslint-disable-next-line 
    }, [currentDeleted])

    useEffect(() => {
        if (!propertyList || propertyList.length === 0) {
            const userProfileId = window.localStorage.getItem("userProfileId");
            userProfileId && dispatch(getPropertySelectionList(userProfileId));
        }
        // eslint-disable-next-line        
    }, []);

    useEffect(() => {
        // Singular property on acc, immediatly select this property and proceed. 
        if (propertyList?.length === 1 && !propertyListLoading) {
            const property = propertyList[0]
            // Make sure we haven't already selected it as default
            if (localStorage.getItem("currentProperty") !== property.roll_num) {
                selectProperty(
                    property.roll_num as string,
                    property.address as string,
                    property.property_id as string,
                    property.owner_type as string,
                    property.property_code as string,
                    property.group_id as string
                );
            }

            // edge initial case where no properties on account on initial load, redirect to profile page ->
            // put user flow into location change effect
        } if (propertyList?.length === 0
            && propertyList
            && !propertyListLoading
        ) {
            navigate("/amp/profile");
            dispatch(changeModalState("modal-fade-out"));
            dispatch(setToastNotification("No Properties on this account!", "warning"));
        }
        // eslint-disable-next-line 
    }, [propertyList, propertyListLoading, dispatch])

    useEffect(() => {
        if (responseStatus && responseStatus !== 200) {
            dispatch(setToastNotification(responseMessage ?? "Something went wrong", "error"));
        }
    }, [responseStatus, dispatch, responseMessage]);

    useEffect(() => {
        // ShowFullVersion was added to make sure the following doesn't trigger for the simple version list
        // This is to prevent document from getting downloaded twice
        if (!downloadPdf.loading && downloadPdf.found && downloadPdf.roll && downloadPdf.pdf && showFullVersion) {
            const tempLink = document.createElement("a");
            tempLink.download = `Property_Profile_Report-${downloadPdf.roll.substring(0, 15)}.pdf`;
            tempLink.href = 'data:application/octet-stream;base64,' + downloadPdf.pdf;
            document.body.appendChild(tempLink);
            tempLink.click();
            tempLink.remove();
            dispatch(resetResidentialProfile());
            const userProfileId = window.localStorage.getItem("userProfileId");
            userProfileId && dispatch(getPropertySelectionList(userProfileId));
        } else if (!downloadPdf.loading && downloadPdf.found === false && showFullVersion) {
            dispatch(setToastNotification("Profile Report not found!", "error"));
        }
    }, [downloadPdf.loading, downloadPdf.pdf, downloadPdf.roll, downloadPdf.found, dispatch, showFullVersion]);

    const displayOwnerType = (ownerCode: string | number): string => {
        switch (ownerCode) {
            case 'own':
                return t("property-list-table.body.column-1-owner") as string;
            case 'ten':
                return t("property-list-table.body.column-1-tenant") as string;
            case 'ttl':
                return t("property-list-table.body.column-1-ttl") as string;
            default:
                return "";
        }
    }

    const timeConverter = (timeString: string | number): string => {
        const dateToConvert = new Date(timeString);
        return dateToConvert.getFullYear() + "-" + (dateToConvert.getMonth() + 1).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }) + "-" + dateToConvert.getDate().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
    }

    const expandCollapseRowHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        const eventTarget = event.currentTarget as Element;
        expandCollapseRow(eventTarget);
        const rootDiv = eventTarget.closest(".property-selection-list-body");
        if (rootDiv) {
            const el = rootDiv.querySelector(".expand-collapse-all-button");
            if (rootDiv.querySelectorAll(".collapsed-table-row").length === 0 && el?.classList.contains("collapsed-all")) {
                el.classList.remove("collapsed-all");
                el.classList.add("expanded-all");
                el.innerHTML = t("property-list-table.header.column-7-2") as string;
            } else if (rootDiv.querySelectorAll(".expanded-table-row").length === 0 && el?.classList.contains("expanded-all")) {
                el.classList.remove("expanded-all");
                el.classList.add("collapsed-all");
                el.innerHTML = t("property-list-table.header.column-7-1") as string;
            }
        }
    }

    const expandCollapseRow = (element: Element) => {
        if (element.classList.contains("expanded-table-row")) {
            element.classList.remove("expanded-table-row");
            element.classList.add("collapsed-table-row");
            element.parentElement?.closest('tr')?.querySelectorAll(".expandable-collapsible").forEach((el) => {
                el.classList.add("hide");
            });
            element.innerHTML = "[ + ]";
        } else if (element.classList.contains("collapsed-table-row")) {
            element.classList.remove("collapsed-table-row");
            element.classList.add("expanded-table-row");
            element.parentElement?.closest('tr')?.querySelectorAll(".expandable-collapsible").forEach((el) => {
                el.classList.remove("hide");
            });
            element.innerHTML = "[ - ]";
        }
    }

    const expandCollapseAll = (event: React.MouseEvent<HTMLButtonElement>) => {
        const eventTarget = event.currentTarget as Element;
        const rootDiv = eventTarget.closest(".property-selection-list-body");
        if (eventTarget.classList.contains("expanded-all")) {
            eventTarget.classList.remove("expanded-all");
            eventTarget.classList.add("collapsed-all");
            eventTarget.innerHTML = t("property-list-table.header.column-7-1") as string;
            rootDiv && rootDiv.querySelectorAll(".expanded-table-row").forEach((el) => {
                expandCollapseRow(el);
            });
        } else if (eventTarget.classList.contains("collapsed-all")) {
            eventTarget.classList.remove("collapsed-all");
            eventTarget.classList.add("expanded-all");
            eventTarget.innerHTML = t("property-list-table.header.column-7-2") as string;
            rootDiv && rootDiv.querySelectorAll(".collapsed-table-row").forEach((el) => {
                expandCollapseRow(el);
            });
        }

    }

    const addProperty = () => {
        const ariaLabelText = t("add-property-modal.title") as string;
        dispatch(alternateAddPropertyKey());
        if (modalContentAriaLabel !== ariaLabelText) {
            dispatch(changeModalContent(<AddProperty />, ariaLabelText, ariaLabelText));
        }
        if (!modalIsCloseable) {
            dispatch(changeIsCloseable(true));
        }
        dispatch(changeModalState("modal-fade-in"));
    }

    const viewPropertyProfile = (roll: string, owner_type: string) => {
        const userProfileId = window.localStorage.getItem("userProfileId");
        let userLanguage = window.localStorage.getItem("prefLang");
        if (!userLanguage) {
            if (manageUserInfoState.pref_lang) {
                userLanguage = manageUserInfoState.pref_lang as string;
            } else {
                userLanguage = "en_EN";
            }
        }
        setCurrentDownload(roll);
        userProfileId && dispatch(downloadResidentialProfilePdf(userProfileId, roll, userLanguage, "N", owner_type));
    }

    const selectProperty = (roll: string, address: string, propertyId: string, ownerType: string, propertyCode: string, type: string, event?: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (event && event.key !== "Enter") {
            return;
        }
        window.localStorage.setItem("currentAddress", address);
        dispatch(setCurrentAddress(address));

        window.localStorage.setItem("currentProperty", roll);
        dispatch(setCurrentProperty(roll));
        dispatch(getCurrentPropertyDetails(roll));

        window.localStorage.setItem("o_type", ownerType);
        dispatch(setCurrentOwnerType(ownerType))

        onPropertySelected && onPropertySelected();

        window.localStorage.setItem("propertyId", propertyId);
        window.localStorage.setItem("propertyCode", propertyCode)
        const userProfileId = window.localStorage.getItem("userProfileId");

        window.localStorage.setItem("propertyType", type)

        if (userProfileId) {
            dispatch(getViewPropertyDetails(userProfileId, roll));
        }
        dispatch(getPropertyDocuments(roll, propertyId));

        if (currentProperty && propertyCode) {
            if (localStorage.getItem("reportDropdown")) {
                localStorage.removeItem("reportDropdown")
            }
            dispatch(getReports(roll.substring(0, 15), propertyCode))
        }

        if (currentProperty) {
            let userLanguage = window.localStorage.getItem('prefLang');
            if (!userLanguage) {
                if (manageUserInfoState.pref_lang) {
                    userLanguage = manageUserInfoState.pref_lang as string;
                } else {
                    userLanguage = 'en_EN';
                }
            }
            dispatch(downloadTenantProfile(roll, userLanguage));
        }

        if (redirects) {
            navigate("/amp/property");
        }
        else {
            dispatch(setRedirects(true));
        }
    }

    const removeProperty = (roll: string, ownerCode: string) => {
        const ariaLabelText = t("remove-property-modal.title") as string;
        dispatch(changeModalContent(<RemoveProperty roll={roll} ownerType={ownerCode} afterRemove={(rollRemoved: string, ownerType: string) => {
            // We are removing the current property
            if (rollRemoved === currentRoll && ownerType === currentOwnerType) {
                localStorage.removeItem("currentProperty");
                localStorage.removeItem("currentAddress");
                localStorage.removeItem("propertyCode");
                localStorage.removeItem("propertyType");
                localStorage.removeItem("propertyId");

                dispatch(setCurrentProperty(""));
                dispatch(setCurrentAddress(""));
                dispatch(setCurrentOwnerType(""));

                setCurrentDeleted(true);
                setLastDeletedRoll(rollRemoved);
                dispatch(setRedirects(false));
            }
        }} />, ariaLabelText, ariaLabelText));
        if (!modalIsCloseable) {
            dispatch(changeIsCloseable(true));
        }
        dispatch(changeModalState("modal-fade-in"));
    }

    const searchProperty = (val: string) => {
        setSearchText(val);
    }

    const sortByCriteria = (event: React.MouseEvent<HTMLButtonElement>, column: 1 | 2 | 3) => {
        const eventTarget = event.currentTarget as Element;
        const rootDiv = eventTarget.closest(".property-selection-list-body");
        rootDiv && rootDiv.querySelectorAll(".sort-arrow-font-awesome-icon").forEach((el) => {
            el.classList.add("hide");
        })
        const ascSortArrow = eventTarget.querySelector(".asc-sort-icon");
        const descSortArrow = eventTarget.querySelector(".desc-sort-icon");
        if (sortCriteria[0] === column && sortCriteria[1] === "ASC") {
            setSortCriteria([column, "DESC"]);
            descSortArrow && (descSortArrow.classList.remove("hide"));
        } else {
            setSortCriteria([column, "ASC"]);
            ascSortArrow && (ascSortArrow.classList.remove("hide"));
        }
    }

    const customSort = (a: UserProperty, b: UserProperty) => {
        if (sortCriteria[0] === 3) {
            if (a.city < b.city) {
                return -1;
            }
            if (a.city > b.city) {
                return 1;
            }
            return 0;
        }
        else if (sortCriteria[0] === 2) {
            if (a.address < b.address) {
                return -1;
            }
            if (a.address > b.address) {
                return 1;
            }
            return 0;
        }
        else {
            if (a.roll_num < b.roll_num) {
                return -1;
            }
            if (a.roll_num > b.roll_num) {
                return 1;
            }
            return 0;
        }
    }

    const renderPropertyListTable = (searchTerm: string) => {
        let finalPropertyList: UserProperty[] = [];
        let totalRecords = 0;
        let totalPages = 1;
        if (propertyList) {
            finalPropertyList = filterPropertyList(propertyList, searchTerm);
            finalPropertyList = finalPropertyList.sort(customSort);
            if (sortCriteria[1] === "DESC") {
                finalPropertyList.reverse();
            }
            totalRecords = finalPropertyList.length;
            totalPages = Math.ceil(totalRecords / recordsPerPage);
        }

        return <>
            <table className="property-selection-list-table">
                <thead>
                    <tr>
                        <th className="table-roll-column">
                            <button onClick={(e) => sortByCriteria(e, 1)} data-testid="sort-by-roll-test">
                                <span>{t("property-list-table.header.column-1") as string}</span>
                                <span className="sort-arrow">
                                    <FontAwesomeIcon className="sort-arrow-font-awesome-icon asc-sort-icon" icon={faSortAsc} />
                                    <FontAwesomeIcon className="sort-arrow-font-awesome-icon desc-sort-icon hide" icon={faSortDesc} />
                                </span>
                            </button>
                        </th>
                        <th>
                            <button onClick={(e) => sortByCriteria(e, 2)} data-testid="sort-by-address-test">
                                <span>{t("property-list-table.header.column-2") as string}</span>
                                <span className="sort-arrow">
                                    <FontAwesomeIcon className="sort-arrow-font-awesome-icon asc-sort-icon hide" icon={faSortAsc} />
                                    <FontAwesomeIcon className="sort-arrow-font-awesome-icon desc-sort-icon hide" icon={faSortDesc} />
                                </span>
                            </button>
                        </th>
                        <th>
                            <button onClick={(e) => sortByCriteria(e, 3)} data-testid="sort-by-city-test">
                                <span>{t("property-list-table.header.column-3") as string}</span>
                                <span className="sort-arrow">
                                    <FontAwesomeIcon className="sort-arrow-font-awesome-icon asc-sort-icon hide" icon={faSortAsc} />
                                    <FontAwesomeIcon className="sort-arrow-font-awesome-icon desc-sort-icon hide" icon={faSortDesc} />
                                </span>
                            </button>
                        </th>
                        {showFullVersion && <th>{t("property-list-table.header.column-4") as string}</th>}
                        {showFullVersion && <th>{t("property-list-table.header.column-5") as string}</th>}
                        {showFullVersion && <th>{t("property-list-table.header.column-6") as string}</th>}
                        {showFullVersion && <th><button onClick={(e) => expandCollapseAll(e)} className="expand-collapse-all-button expanded-all">{t("property-list-table.header.column-7-2") as string}</button></th>}
                    </tr>
                </thead>
                <tbody>
                    {finalPropertyList.map((property, index) => {
                        if ((index >= ((currentPage - 1) * recordsPerPage)) && (index < (currentPage * recordsPerPage))) {
                            return (
                                <tr
                                    key={`${property.roll_num}_${property.owner_type}`}
                                    className={(currentRoll &&
                                        property.roll_num === currentRoll && property.owner_type === currentOwnerType) ? "selected-roll-row" : undefined}
                                    onClick={((showFullVersion || (currentRoll && property.roll_num === currentRoll && property.owner_type === currentOwnerType)) ? undefined : () => selectProperty(property.roll_num as string, property.address as string, property.property_id as string, property.owner_type as string, property.property_code as string, property.group_id as string))}
                                    onKeyDown={((showFullVersion || (currentRoll && property.roll_num === currentRoll && property.owner_type === currentOwnerType)) ? undefined : (e) => selectProperty(property.roll_num as string, property.address as string, property.property_id as string, property.owner_type as string, property.property_code as string, property.group_id as string))}
                                    tabIndex={(showFullVersion || (currentRoll && property.roll_num === currentRoll && property.owner_type === currentOwnerType)) ? undefined : 0}
                                >
                                    <td className="table-roll-column">
                                        <div>{property.roll_num}</div>
                                        <div className="expandable-collapsible"><span className="hyperlink-like-text">{t("property-list-table.body.column-1") as string}:</span> {displayOwnerType(property.owner_type)}</div>
                                        {(property.is_shared === "1") ? <div className="expandable-collapsible">{t("property-list-table.body.column-1-shared") as string}</div> : ""}
                                    </td>
                                    <td>
                                        {property.address}
                                    </td>
                                    <td>
                                        {property.city}
                                    </td>
                                    {showFullVersion && <td>
                                        {/* Need Href For Accessibiltiy */}
                                        {/* eslint-disable-next-line */}
                                        {(downloadPdf.loading && currentDownload === property.roll_num) ? <AmpInlineLoadingSpinner /> : <a tabIndex={0} onClick={() => viewPropertyProfile(property.roll_num as string, property.owner_type)}>PDF</a>}
                                    </td>}
                                    {showFullVersion && <td>
                                        {(property.reported_at === null) ? "N/A" : timeConverter(property.reported_at)}
                                    </td>}
                                    {showFullVersion && <td>
                                        {/* eslint-disable-next-line */}
                                        <a
                                            className="select-property-anchor"
                                            tabIndex={0}
                                            onClick={
                                                (currentRoll && property.roll_num === currentRoll && property.owner_type === currentOwnerType) ?
                                                    undefined :
                                                    () => selectProperty(property.roll_num as string, property.address as string, property.property_id as string, property.owner_type as string, property.property_code as string, property.group_id as string)}
                                        >
                                            {t("property-list-table.body.column-6-1") as string}
                                            {/* eslint-disable-next-line */}
                                        </a> | <a tabIndex={0} onClick={() => removeProperty(property.roll_num as string, property.owner_type as string)}>{t("property-list-table.body.column-6-2") as string}</a>
                                    </td>}
                                    {showFullVersion && <td>
                                        <button onClick={(e) => expandCollapseRowHandler(e)} className="expand-collapse-button expanded-table-row">[ - ]</button>
                                    </td>}
                                </tr>);
                        } else {
                            return <></>;
                        }
                    })}
                </tbody>
            </table>
            <div className="property-selection-list-footer">
                <AmpPagination
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    totalRecords={totalRecords}
                    recordsPerPage={recordsPerPage}
                />
                {showFullVersion && <div className="add-property-button">
                    <AmpButton onClick={addProperty} type="submit" text={t("property-list-table.add-property-button") as string} />
                </div>}
            </div>
        </>
    }

    return (
        <>
            <AmpLoadingSpinner loading={propertyListLoading} >
                <div className={"property-selection-list-body" + (showFullVersion ? "" : " simple-version-table")}>
                    <div className="property-selection-list-search-container">
                        {showFullVersion && <div className="add-property-description-text">
                            {(
                                <Trans defaults={t("add-property-text") as string} >
                                    <strong data-testid={"add-property-text"}></strong>
                                </Trans>
                            )}
                        </div>}
                        <div className="property-selection-list-search">
                            <div className="search-icon"><FontAwesomeIcon className="search-font-awesome-icon" icon={faMagnifyingGlass} /></div>
                            <AmpInput name="searchBox" id="searchBox" type="text" className="search-box" placeholder={t("search-input-placeholder") as string} onInput={searchProperty} noTimer={true} ariaLabel={t("search-input-placeholder") as string} />
                        </div>
                    </div>
                    {renderPropertyListTable(searchText)}
                </div>
            </AmpLoadingSpinner>
        </>
    );
}
